import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import IncontiLogo from "@/components/IncontiLogo";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, loginVerify2FA } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=credentials, 2=2fa code
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [twofaToken, setTwofaToken] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.twofa_required) {
        setTwofaToken(res.twofa_token);
        setStep(2);
        return;
      }
      navigate(res?.role === "mecanico" ? "/workshop" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Error de inicio de sesión");
    } finally {
      setLoading(false);
    }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await loginVerify2FA(twofaToken, code.trim());
      navigate(u?.role === "mecanico" ? "/workshop" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Código incorrecto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 grid-bg relative">
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(circle at 50% 50%, transparent, rgba(5,8,18,0.95))",
      }} />
      <div className="w-full max-w-md panel-strong relative scan-line">
        <div className="px-8 pt-8 pb-6 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center justify-between mb-6">
            <IncontiLogo size={40} />
            <div className="flex items-center gap-1.5 text-[10px] label-mono">
              <span className="live-dot" /> ONLINE
            </div>
          </div>
          <div className="label-mono">// {step === 1 ? "Acceso al sistema" : "Verificación 2FA"}</div>
          <h1 className="font-display font-black text-3xl tracking-wide mt-1 text-slate-100">
            {step === 1 ? "Iniciar sesión" : "Código de verificación"}
          </h1>
          <p className="text-sm text-slate-400 mt-2">
            {step === 1
              ? "Sistema de gestión integral CORPORATION INCONTI."
              : "Introduce el código de 6 dígitos de tu app autenticadora o un código de respaldo."}
          </p>
        </div>

        {step === 1 ? (
          <form onSubmit={submit} className="px-8 py-6 space-y-4" autoComplete="off">
            {/* Honeypot fields to deter aggressive browser autofill */}
            <input type="text" name="fakeuser" autoComplete="username" tabIndex={-1}
              style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
              aria-hidden="true" />
            <input type="password" name="fakepass" autoComplete="current-password" tabIndex={-1}
              style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0 }}
              aria-hidden="true" />
            <div>
              <label className="label-mono block mb-1.5">Correo electrónico</label>
              <input type="email" required data-testid="login-email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                name="inconti-login-email"
                autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
                className="w-full input-cyber px-3 py-2.5 text-sm" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Contraseña</label>
              <input type="password" required data-testid="login-password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                name="inconti-login-password"
                autoComplete="new-password" autoCorrect="off" autoCapitalize="off" spellCheck="false"
                className="w-full input-cyber px-3 py-2.5 text-sm" />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm px-3 py-2 rounded-sm badge-danger">{error}</div>
            )}

            <button type="submit" data-testid="login-submit-btn" disabled={loading}
              className="w-full btn-primary py-2.5 rounded-sm tracking-widest text-sm uppercase disabled:opacity-50 mt-2">
              {loading ? "Ingresando…" : "Ingresar"}
            </button>

            <div className="text-center text-[11px] text-slate-500 pt-2 label-mono">
              // Solicita acceso al administrador del sistema
            </div>
          </form>
        ) : (
          <form onSubmit={submit2FA} className="px-8 py-6 space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-sm" style={{ background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.2)" }}>
              <ShieldCheck className="w-5 h-5" style={{ color: "var(--cyan)" }} />
              <div className="text-xs text-slate-300">
                Tu cuenta tiene <b>autenticación en 2 pasos</b> activa.
                <div className="text-[10px] text-slate-500 mt-0.5">{email}</div>
              </div>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Código (6 dígitos o respaldo)</label>
              <input
                type="text"
                required
                inputMode="numeric"
                pattern="[A-Z0-9-]*"
                autoFocus
                data-testid="login-2fa-code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="123456"
                maxLength={12}
                className="w-full input-cyber px-3 py-2.5 text-center tracking-[0.4em] text-xl font-mono"
              />
              <div className="text-[10px] text-slate-500 mt-2">
                ¿Perdiste tu app? Puedes usar un código de respaldo (formato XXXXX-XXXXX).
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm px-3 py-2 rounded-sm badge-danger">{error}</div>
            )}

            <button type="submit" data-testid="login-2fa-submit" disabled={loading || code.length < 6}
              className="w-full btn-primary py-2.5 rounded-sm tracking-widest text-sm uppercase disabled:opacity-50 mt-2">
              {loading ? "Verificando…" : "Verificar"}
            </button>
            <button type="button" onClick={() => { setStep(1); setCode(""); setError(""); }}
              className="w-full text-[11px] text-slate-500 hover:text-slate-300 label-mono">
              ← Volver a iniciar sesión
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
